import { useState } from "react";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { Loader2, ArrowLeft, CalendarDays, Ticket, Sparkles, StickyNote, ClipboardList, Send, RefreshCcw, Trash2, FileDown, Receipt, CreditCard, Plus, X, AlertCircle } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { fmtDate, fmtTimeRange, money } from "@/lib/format";

const fmtDur = (iso) => {
  if (!iso) return "—";
  const days = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 86400000));
  if (days < 31) return `${days} day${days === 1 ? "" : "s"}`;
  if (days < 365) return `${Math.floor(days / 30)} month${Math.floor(days / 30) === 1 ? "" : "s"}`;
  const y = Math.floor(days / 365), m = Math.floor((days % 365) / 30);
  return `${y} yr${y > 1 ? "s" : ""}${m ? ` ${m} mo` : ""}`;
};

const STATUS_TONE = {
  confirmed: "bg-[#5C8A72]/15 text-[#5C8A72]", completed: "bg-[#144F75]/10 text-[#144F75]",
  cancelled: "bg-red-100 text-red-500", no_show: "bg-[#D98E73]/15 text-[#B5794E]", pending: "bg-[#E0A458]/15 text-[#B5794E]",
};

// ─── Service Credits Section ──────────────────────────────────────────────────
function ServiceCreditsSection({ userId }) {
  const qc = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ credit_name: "", sessions: 1, session_value: 75, expires_at: "", reason: "", eligible_service_ids: [] });

  const { data: creditsData, isLoading: creditsLoading } = useQuery({
    queryKey: ["member360-credits", userId],
    queryFn: () => api.get(`/crm/member360/${userId}/service-credits`).then((r) => r.data),
  });

  // Use the member's primary center from the credits endpoint for scoped service loading
  const centerLocationId = creditsData?.member_center_id || "";

  const { data: svcsData = [] } = useQuery({
    queryKey: ["services-list", centerLocationId],
    queryFn: () => {
      const url = centerLocationId
        ? `/services?location_id=${encodeURIComponent(centerLocationId)}&published_only=true`
        : "/services";
      return api.get(url).then((r) => r.data || []);
    },
    enabled: showForm,   // only load when form is open to save bandwidth
  });

  const grantMut = useMutation({
    mutationFn: (data) => api.post(`/crm/member360/${userId}/service-credits`, data),
    onSuccess: () => {
      toast.success("Service credit granted.");
      setShowForm(false);
      setForm({ credit_name: "", sessions: 1, session_value: 75, expires_at: "", reason: "", eligible_service_ids: [] });
      qc.invalidateQueries({ queryKey: ["member360-credits", userId] });
      qc.invalidateQueries({ queryKey: ["member360", userId] });
    },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });

  const revokeMut = useMutation({
    mutationFn: (creditId) => api.delete(`/crm/member360/${userId}/service-credits/${creditId}`),
    onSuccess: () => { toast.success("Credit voided."); qc.invalidateQueries({ queryKey: ["member360-credits", userId] }); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });

  const toggleSvc = (id) => setForm((f) => ({
    ...f,
    eligible_service_ids: f.eligible_service_ids.includes(id)
      ? f.eligible_service_ids.filter((x) => x !== id)
      : [...f.eligible_service_ids, id],
  }));

  const credits = creditsData?.credits || [];
  const activeCredits = credits.filter((c) => c.sessions_remaining > 0 && !c.voided && (!c.expires_at || c.expires_at >= new Date().toISOString().slice(0, 10)));
  const inactiveCredits = credits.filter((c) => c.sessions_remaining <= 0 || c.voided || (c.expires_at && c.expires_at < new Date().toISOString().slice(0, 10)));

  return (
    <div className="eyw-card p-6 mt-6" data-testid="member360-service-credits">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-serif text-lg text-[#1A242B] flex items-center gap-2">
          <CreditCard size={17} className="text-[#5C8A72]" /> Service credits
        </h3>
        <button onClick={() => setShowForm(!showForm)} data-testid="member360-grant-credit-btn"
          className="eyw-btn !py-1.5 text-sm inline-flex items-center gap-1.5">
          {showForm ? <X size={14} /> : <Plus size={14} />} {showForm ? "Cancel" : "Grant credit"}
        </button>
      </div>

      {showForm && (
        <div className="bg-[#F5F8FA] rounded-xl p-5 mb-5 border border-[#144F75]/10" data-testid="grant-credit-form">
          <div className="grid sm:grid-cols-2 gap-3 mb-3">
            <div>
              <label className="text-xs font-medium text-[#4A5D6B] mb-1 block">Credit name *</label>
              <input value={form.credit_name} onChange={(e) => setForm({ ...form, credit_name: e.target.value })}
                placeholder="e.g. Float Therapy Comp" data-testid="credit-name-input" className="eyw-input w-full text-sm" />
            </div>
            <div>
              <label className="text-xs font-medium text-[#4A5D6B] mb-1 block">Sessions *</label>
              <input type="number" min="1" max="50" value={form.sessions}
                onChange={(e) => setForm({ ...form, sessions: Number(e.target.value) })}
                data-testid="credit-sessions-input" className="eyw-input w-full text-sm" />
            </div>
            <div>
              <label className="text-xs font-medium text-[#4A5D6B] mb-1 block">Session value ($)</label>
              <input type="number" min="0" step="0.01" value={form.session_value}
                onChange={(e) => setForm({ ...form, session_value: Number(e.target.value) })}
                data-testid="credit-value-input" className="eyw-input w-full text-sm" />
              <p className="text-[10px] text-[#8FA89B] mt-1">Used to calculate upcharges for premium services</p>
            </div>
            <div>
              <label className="text-xs font-medium text-[#4A5D6B] mb-1 block">Expiry date (optional)</label>
              <input type="date" value={form.expires_at} onChange={(e) => setForm({ ...form, expires_at: e.target.value })}
                data-testid="credit-expiry-input" className="eyw-input w-full text-sm" />
            </div>
          </div>
          <div className="mb-3">
            <label className="text-xs font-medium text-[#4A5D6B] mb-1 block">
              Applicable services <span className="text-[#8FA89B] font-normal">(leave blank = any service)</span>
              {centerLocationId && <span className="ml-2 text-[10px] text-[#5C8A72]">Showing center-specific + shared services</span>}
            </label>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-1.5 max-h-48 overflow-y-auto p-1" data-testid="credit-service-picker">
              {svcsData.filter((s) => s.active !== false).map((s) => (
                <label key={s.id} className={`flex items-center gap-2 text-xs rounded-lg border px-2.5 py-1.5 cursor-pointer transition-colors ${form.eligible_service_ids.includes(s.id) ? "border-[#5C8A72] bg-[#5C8A72]/10 text-[#1A242B]" : "border-[#E5E0D8] bg-white text-[#4A5D6B] hover:bg-[#F9F7F3]"}`}
                  data-testid={`credit-svc-${s.id}`}>
                  <input type="checkbox" checked={form.eligible_service_ids.includes(s.id)} onChange={() => toggleSvc(s.id)} className="accent-[#5C8A72]" />
                  <span className="truncate">{s.title}</span>
                </label>
              ))}
            </div>
            {form.eligible_service_ids.length === 0 && (
              <div className="flex items-center gap-1.5 text-xs text-[#8FA89B] mt-1.5">
                <AlertCircle size={11} /> No services selected — this credit applies to any service
              </div>
            )}
          </div>
          <div className="mb-3">
            <label className="text-xs font-medium text-[#4A5D6B] mb-1 block">Reason / internal note</label>
            <input value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })}
              placeholder="e.g. Comp for scheduling issue" data-testid="credit-reason-input" className="eyw-input w-full text-sm" />
          </div>
          <button onClick={() => grantMut.mutate(form)} disabled={grantMut.isPending || !form.credit_name.trim()}
            data-testid="credit-grant-submit" className="eyw-btn text-sm inline-flex items-center gap-2 disabled:opacity-50">
            {grantMut.isPending ? <Loader2 size={14} className="animate-spin" /> : <CreditCard size={14} />} Grant credit
          </button>
        </div>
      )}

      {creditsLoading ? (
        <p className="text-sm text-[#4A5D6B] flex items-center gap-2"><Loader2 size={15} className="animate-spin" /> Loading…</p>
      ) : (
        <div className="space-y-2">
          {activeCredits.map((c) => (
            <div key={c.id} className="flex items-start gap-3 rounded-xl bg-[#5C8A72]/5 border border-[#5C8A72]/20 px-4 py-3" data-testid={`m360-credit-${c.id}`}>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-[#1A242B]">{c.package_name}</p>
                <p className="text-xs text-[#5C8A72] font-semibold">{c.sessions_remaining}/{c.sessions_total} session{c.sessions_total === 1 ? "" : "s"} remaining</p>
                <p className="text-xs text-[#8FA89B] mt-0.5">
                  {c.eligible_service_titles?.length > 0 ? `For: ${c.eligible_service_titles.join(", ")}` : "Applies to any service"}
                  {c.expires_at ? ` · Expires ${String(c.expires_at).slice(0, 10)}` : ""}
                  {c.reason ? ` · ${c.reason}` : ""}
                  {c.source === "manual_grant" ? ` · Granted by ${c.granted_by_name || "staff"}` : ""}
                </p>
              </div>
              {c.source === "manual_grant" && (
                <button onClick={() => { if (window.confirm("Void this credit?")) revokeMut.mutate(c.id); }}
                  data-testid={`m360-credit-revoke-${c.id}`} title="Void credit" disabled={revokeMut.isPending}
                  className="text-[#C9C2B6] hover:text-red-500 transition-colors mt-0.5"><Trash2 size={15} /></button>
              )}
            </div>
          ))}
          {inactiveCredits.length > 0 && (
            <details className="mt-2">
              <summary className="text-xs text-[#8FA89B] cursor-pointer select-none">Show {inactiveCredits.length} used/expired/voided</summary>
              <div className="space-y-1.5 mt-2">
                {inactiveCredits.map((c) => (
                  <div key={c.id} className="flex items-start gap-3 rounded-xl bg-[#F4F1EB] border border-[#E5E0D8] px-4 py-2.5 opacity-60" data-testid={`m360-credit-used-${c.id}`}>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-[#4A5D6B]">{c.package_name}</p>
                      <p className="text-xs text-[#8FA89B]">{c.voided ? "Voided" : c.sessions_remaining <= 0 ? "Fully used" : "Expired"} · {c.sessions_remaining}/{c.sessions_total} remaining</p>
                    </div>
                  </div>
                ))}
              </div>
            </details>
          )}
          {credits.length === 0 && !showForm && (
            <p className="text-sm text-[#8FA89B]">No service credits on file. Use "Grant credit" to add one.</p>
          )}
        </div>
      )}
    </div>
  );
}


export const Member360 = ({ userId, onBack }) => {
  const qc = useQueryClient();
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);
  const { data, isLoading, error } = useQuery({
    queryKey: ["member360", userId],
    queryFn: () => api.get(`/crm/member360/${userId}`).then((r) => r.data),
    enabled: !!userId,
  });

  if (isLoading) return <div className="py-16 grid place-items-center"><Loader2 className="animate-spin text-[#144F75]" size={28} /></div>;
  if (error || !data) return <p className="text-sm text-[#B5794E] py-8" data-testid="member360-error">{formatApiError(error?.response?.data?.detail) || "Couldn't load this member's profile."}</p>;

  const u = data.user, s = data.stats;
  const addNote = async () => {
    if (!note.trim()) return;
    setSaving(true);
    try {
      await api.post(`/crm/member360/${userId}/notes`, { body: note, category: "session" });
      setNote("");
      toast.success("Note saved");
      qc.invalidateQueries({ queryKey: ["member360", userId] });
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const requestUpdate = async () => {
    try {
      const { data: r } = await api.post(`/crm/member360/${userId}/request-intake-update`, { note: "" });
      toast.success(r.already ? "An update is already requested." : "Intake update requested — they'll see the form at their next booking.");
      qc.invalidateQueries({ queryKey: ["member360", userId] });
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const cancelUpdate = async () => {
    try {
      await api.post(`/crm/member360/${userId}/cancel-intake-update`);
      toast.success("Intake update request cancelled.");
      qc.invalidateQueries({ queryKey: ["member360", userId] });
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const deleteNote = async (noteId) => {
    try {
      await api.delete(`/crm/member360/${userId}/notes/${noteId}`);
      toast.success("Note removed.");
      qc.invalidateQueries({ queryKey: ["member360", userId] });
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const downloadPdf = async () => {
    setPdfBusy(true);
    try {
      const res = await api.get(`/crm/member360/${userId}/intake-pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `intake-${(u.name || "client").toLowerCase().replace(/[^a-z0-9]+/g, "-")}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Intake & notes PDF downloaded.");
    } catch (err) { toast.error("Couldn't generate the PDF. Please try again."); }
    finally { setPdfBusy(false); }
  };

  const downloadCsv = async () => {
    try {
      const res = await api.get(`/crm/member360/${userId}/purchases.csv`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `purchases-${(u.name || "client").toLowerCase().replace(/[^a-z0-9]+/g, "-")}.csv`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
      toast.success("Purchase history CSV downloaded.");
    } catch (err) { toast.error("Couldn't export the CSV."); }
  };

  return (
    <div data-testid="member360">
      {onBack && (
        <button onClick={onBack} data-testid="member360-back" className="text-sm text-[#4A5D6B] hover:text-[#144F75] flex items-center gap-1 mb-4"><ArrowLeft size={15} /> Back</button>
      )}
      <div className="eyw-card p-6 flex flex-wrap items-center gap-5">
        {u.avatar ? <img src={u.avatar} alt={u.name} className="h-16 w-16 rounded-full object-cover" />
          : <span className="grid place-items-center h-16 w-16 rounded-full bg-[#144F75]/10 text-[#144F75] text-xl font-serif">{(u.name || u.email || "?")[0].toUpperCase()}</span>}
        <div className="min-w-0 flex-1">
          <h2 className="font-serif text-2xl text-[#1A242B]" data-testid="member360-name">{u.name || u.email}</h2>
          <p className="text-sm text-[#4A5D6B]">{u.email}{u.phone ? ` · ${u.phone}` : ""}</p>
          <p className="text-xs text-[#8FA89B] mt-1">
            <span className="capitalize">{u.membership_tier || u.role}</span> member · {fmtDur(u.member_since)} with us
            {u.member_since ? ` (since ${String(u.member_since).slice(0, 10)})` : ""}
          </p>
        </div>
        <button onClick={downloadPdf} disabled={pdfBusy} data-testid="member360-export-pdf" title="Download intake answers & notes as a printable PDF"
          className="eyw-btn-ghost !py-2 text-sm shrink-0 disabled:opacity-60">
          {pdfBusy ? <Loader2 className="animate-spin" size={15} /> : <FileDown size={15} />} Intake PDF
        </button>
        {(data.scope === "admin" || data.scope === "manager") && (
          <button onClick={downloadCsv} data-testid="member360-export-csv" title="Download purchase history & package balances as CSV"
            className="eyw-btn-ghost !py-2 text-sm shrink-0">
            <FileDown size={15} /> Purchases CSV
          </button>
        )}
        {data.intake_update_requested && (
          <span className="text-xs px-3 py-1.5 rounded-full bg-[#E0A458]/15 text-[#B5794E] inline-flex items-center gap-2" data-testid="member360-intake-pending">
            Intake update pending
            <button onClick={cancelUpdate} data-testid="member360-cancel-intake" className="underline hover:no-underline">Cancel</button>
          </span>
        )}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4" data-testid="member360-stats">
        {[["Sessions completed", s.sessions_completed], ["Upcoming", s.sessions_upcoming], ["Events attended", s.events_attended], ["Active packages", s.packages_active]].map(([l, v]) => (
          <div key={l} className="eyw-card p-4 text-center">
            <p className="text-2xl font-serif text-[#144F75]">{v}</p>
            <p className="text-xs text-[#8FA89B] mt-0.5">{l}</p>
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-6 mt-6">
        <div className="eyw-card p-6" data-testid="member360-sessions">
          <h3 className="font-serif text-lg text-[#1A242B] flex items-center gap-2 mb-4"><CalendarDays size={17} className="text-[#144F75]" /> Session history</h3>
          <div className="space-y-2.5 max-h-96 overflow-y-auto pr-1">
            {data.sessions.map((b) => (
              <div key={b.id} className="flex items-center gap-3 border-b border-[#E5E0D8] pb-2.5 last:border-0" data-testid={`m360-session-${b.id}`}>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-[#1A242B] truncate">{b.service_title}</p>
                  <p className="text-xs text-[#8FA89B]">{fmtDate(b.date)} {fmtTimeRange(b.start_time)}{b.enhancer_name ? ` · ${b.enhancer_name}` : ""}{b.location ? ` · ${b.location}` : ""}</p>
                </div>
                <span className={`text-[11px] px-2 py-0.5 rounded-full shrink-0 ${STATUS_TONE[b.status] || "bg-[#E5E0D8] text-[#4A5D6B]"}`}>{b.status}</span>
              </div>
            ))}
            {data.sessions.length === 0 && <p className="text-sm text-[#8FA89B]">No sessions yet.</p>}
          </div>
        </div>

        <div className="space-y-6">
          <div className="eyw-card p-6" data-testid="member360-packages">
            <h3 className="font-serif text-lg text-[#1A242B] flex items-center gap-2 mb-3"><Ticket size={17} className="text-[#5C8A72]" /> Packages</h3>
            {data.packages.map((c) => (
              <div key={c.id} className="border-b border-[#E5E0D8] py-2 last:border-0" data-testid={`m360-package-${c.id}`}>
                <div className="flex items-center justify-between">
                  <p className="text-sm text-[#1A242B]">{c.package_name}</p>
                  <span className={`text-sm font-serif ${c.sessions_remaining > 0 ? "text-[#5C8A72]" : "text-[#8FA89B]"}`}>{c.sessions_remaining}/{c.sessions_total} left</span>
                </div>
                <p className="text-xs text-[#8FA89B]">{c.sessions_used ?? Math.max(0, (c.sessions_total || 0) - (c.sessions_remaining || 0))} used{c.expires_at ? ` · expires ${String(c.expires_at).slice(0, 10)}` : ""}</p>
              </div>
            ))}
            {data.packages.length === 0 && <p className="text-sm text-[#8FA89B]">No packages purchased.</p>}
          </div>
          <div className="eyw-card p-6" data-testid="member360-events">
            <h3 className="font-serif text-lg text-[#1A242B] flex items-center gap-2 mb-3"><Sparkles size={17} className="text-[#D98E73]" /> Events & classes</h3>
            <div className="space-y-2 max-h-52 overflow-y-auto pr-1">
              {data.events.map((e) => (
                <div key={e.id} className="flex items-center justify-between border-b border-[#E5E0D8] pb-2 last:border-0" data-testid={`m360-event-${e.id}`}>
                  <div><p className="text-sm text-[#1A242B]">{e.event_title}</p><p className="text-xs text-[#8FA89B]">{fmtDate(e.date)}</p></div>
                  {e.checked_in && <span className="text-[11px] px-2 py-0.5 rounded-full bg-[#5C8A72]/15 text-[#5C8A72]">attended</span>}
                </div>
              ))}
              {data.events.length === 0 && <p className="text-sm text-[#8FA89B]">No event registrations.</p>}
            </div>
          </div>
        </div>
      </div>

      {(data.scope === "admin" || data.scope === "manager") && (
        <ServiceCreditsSection userId={userId} />
      )}

      <div className="eyw-card p-6 mt-6" data-testid="member360-purchases">
        <h3 className="font-serif text-lg text-[#1A242B] flex items-center gap-2 mb-4"><Receipt size={17} className="text-[#B5794E]" /> Purchase history</h3>
        <div className="max-h-96 overflow-y-auto pr-1">
          {(data.purchases || []).map((p) => (
            <div key={p.id} className="flex items-center gap-3 border-b border-[#E5E0D8] py-2.5 last:border-0" data-testid={`m360-purchase-${p.id}`}>
              <span className="text-[10px] uppercase px-2 py-0.5 rounded-full bg-[#F1EDE6] text-[#8FA89B] shrink-0 w-20 text-center truncate">{p.type}</span>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-[#1A242B] truncate">{p.title}{p.qty > 1 ? ` × ${p.qty}` : ""}</p>
                <p className="text-xs text-[#8FA89B] truncate">{fmtDate(p.date)}{p.package_name ? ` · ${p.package_name}` : ""}{p.enhancer_name ? ` · ${p.enhancer_name}` : ""}{p.center ? ` · ${p.center}` : ""} · {p.source}</p>
              </div>
              <span className="text-sm font-serif text-[#144F75] shrink-0">{p.amount > 0 ? `${money(Number(p.amount))}` : "credit"}</span>
            </div>
          ))}
          {(data.purchases || []).length === 0 && <p className="text-sm text-[#8FA89B]">No purchases yet.</p>}
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6 mt-6">
        <div className="eyw-card p-6" data-testid="member360-intake">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-serif text-lg text-[#1A242B] flex items-center gap-2"><ClipboardList size={17} className="text-[#144F75]" /> Intake responses</h3>
            <button onClick={requestUpdate} data-testid="member360-request-intake" className="text-xs text-[#144F75] font-medium hover:underline flex items-center gap-1"><RefreshCcw size={12} /> Request update</button>
          </div>
          <div className="space-y-4 max-h-80 overflow-y-auto pr-1">
            {data.intake_responses.map((i) => (
              <div key={i.id} className="rounded-xl bg-[#F9F7F3] border border-[#E5E0D8] p-4" data-testid={`m360-intake-${i.id}`}>
                <p className="text-xs text-[#8FA89B] mb-2">{String(i.created_at).slice(0, 10)}{i.enhancer_name ? ` · for ${i.enhancer_name}` : ""}{i.service_title ? ` · ${i.service_title}` : ""}</p>
                {i.answers.map((a, idx) => (
                  <div key={idx} className="mb-1.5"><p className="text-xs font-medium text-[#4A5D6B]">{a.label}</p><p className="text-sm text-[#1A242B]">{a.answer || "—"}</p></div>
                ))}
              </div>
            ))}
            {data.intake_responses.length === 0 && <p className="text-sm text-[#8FA89B]">No intake form on file yet.</p>}
          </div>
        </div>

        <div className="eyw-card p-6" data-testid="member360-notes">
          <h3 className="font-serif text-lg text-[#1A242B] flex items-center gap-2 mb-3"><StickyNote size={17} className="text-[#B5794E]" /> Practitioner notes</h3>
          <div className="flex gap-2 mb-4">
            <textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add a session note… (visible to care team, never to the member)" data-testid="member360-note-input" className="eyw-input flex-1 resize-none" />
            <button onClick={addNote} disabled={saving || !note.trim()} data-testid="member360-note-save" className="eyw-btn !px-4 self-end disabled:opacity-50">{saving ? <Loader2 className="animate-spin" size={15} /> : <Send size={15} />}</button>
          </div>
          <div className="space-y-3 max-h-72 overflow-y-auto pr-1">
            {(data.notes || []).map((n) => (
              <div key={n.id} className="rounded-xl bg-[#F9F7F3] border border-[#E5E0D8] p-3.5 group relative" data-testid={`m360-note-${n.id}`}>
                <p className="text-sm text-[#1A242B] whitespace-pre-wrap pr-6">{n.body}</p>
                <p className="text-[11px] text-[#8FA89B] mt-1.5">{n.author_name || "Staff"} · {String(n.created_at).slice(0, 10)} · <span className="capitalize">{n.category?.replace("_", " ")}</span></p>
                <button onClick={() => deleteNote(n.id)} data-testid={`m360-note-delete-${n.id}`} title="Remove note"
                  className="absolute top-3 right-3 text-[#C9C2B6] hover:text-red-500 opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity"><Trash2 size={14} /></button>
              </div>
            ))}
            {(data.notes || []).length === 0 && <p className="text-sm text-[#8FA89B]">No notes yet.</p>}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Member360;
